/**
 * @author      Antigravity AI
 * @date        June, 2026
 * @copyright   www.ibirdsservices.com
 */

import React, { useState, useEffect } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import WhatsAppAPI from "../../api/WhatsAppAPI";

const InstagramSettingEdit = (props) => {
  const [isSpinner, setIsSpinner] = useState(false);
  const [whatsappSettings, setWhatsappSettings] = useState([]);
  const [settingData, setSettingData] = useState({
    id: props?.rowData?.id || "",
    name: props?.rowData?.name || "",
    app_id: props?.rowData?.app_id || "",
    app_secret: props?.rowData?.app_secret || "",
    access_token: props?.rowData?.access_token || "",
    verify_token: props?.rowData?.verify_token || "",
    page_id: props?.rowData?.page_id || "",
    business_number: props?.rowData?.business_number || "",
  });

  useEffect(() => {
    const fetchWhatsAppSettings = async () => {
      try {
        const res = await WhatsAppAPI.getWhatsAppSettingRecord();
        if (res && res.success && Array.isArray(res.record)) {
          setWhatsappSettings(res.record);
        }
      } catch (err) {
        console.error("Error fetching whatsapp settings:", err);
      }
    };
    fetchWhatsAppSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettingData({ ...settingData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const isValid =
      settingData.name.trim() &&
      settingData.app_id.trim() &&
      settingData.app_secret.trim() &&
      settingData.access_token.trim() &&
      settingData.verify_token.trim() &&
      settingData.page_id.trim();

    if (!isValid) {
      toast.error("Required fields are missing.");
      return;
    }

    setIsSpinner(true);

    try {
      const result = settingData.id
        ? await WhatsAppAPI.updateInstagramSettingRecord(settingData)
        : await WhatsAppAPI.insertInstagramSettingRecords(settingData);

      if (result.success) {
        toast.success(
          settingData.id
            ? "Record updated successfully."
            : "Record created successfully."
        );
        setTimeout(() => {
          props.onRefreshData();
          setIsSpinner(false);
        }, 1000);
      } else {
        setIsSpinner(false);
        toast.error(result.message || "Failed to save record.");
      }
    } catch (error) {
      setIsSpinner(false);
      console.error("Error during save operation", error);
      toast.error("An unexpected error occurred while saving the record.");
    }
  };

  return (
    <>
      <Modal show={props.show} animation={false} size="lg" centered>
        <Modal.Header closeButton onClick={props.onHide}>
          <Modal.Title id="contained-modal-title-vcenter">
            {settingData?.id ? "Edit Instagram Setting" : "Add Instagram Setting"}
          </Modal.Title>
        </Modal.Header>

        {!isSpinner ? (
          <>
            <Modal.Body>
              <Form noValidate>
                <Row className="p-2 mb-3">
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="mb-3" controlId="formInstagramName">
                      <Form.Label>Setting Name</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        name="name"
                        value={settingData?.name}
                        onChange={handleChange}
                        placeholder="e.g. Primary Instagram LeadAds"
                        style={{ height: "36px" }}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="mb-3" controlId="formAppId">
                      <Form.Label>Facebook App ID</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        name="app_id"
                        value={settingData?.app_id}
                        onChange={handleChange}
                        placeholder="Enter Facebook App ID"
                        style={{ height: "36px" }}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="mb-3" controlId="formAppSecret">
                      <Form.Label>Facebook App Secret</Form.Label>
                      <Form.Control
                        type="password"
                        required
                        name="app_secret"
                        value={settingData?.app_secret}
                        onChange={handleChange}
                        placeholder="Enter Facebook App Secret"
                        style={{ height: "36px" }}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="mb-3" controlId="formPageId">
                      <Form.Label>Facebook Page ID</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        name="page_id"
                        value={settingData?.page_id}
                        onChange={handleChange}
                        placeholder="Enter Facebook Page ID"
                        style={{ height: "36px" }}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="mb-3" controlId="formBusinessNumber">
                      <Form.Label>Default Business Number</Form.Label>
                      <Form.Select
                        name="business_number"
                        value={settingData?.business_number || ""}
                        onChange={handleChange}
                        style={{ height: "36px" }}
                      >
                        <option value="">Select Default Business Number</option>
                        {whatsappSettings.map((wh) => (
                          <option key={wh.id} value={wh.phone}>
                            {wh.phone} {wh.name ? `(${wh.name})` : ""}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col lg={12} sm={12} xs={12}>
                    <Form.Group className="mb-3" controlId="formVerifyToken">
                      <Form.Label>Webhook Verify Token</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        name="verify_token"
                        value={settingData?.verify_token}
                        onChange={handleChange}
                        placeholder="e.g. 123456 or a secure string"
                        style={{ height: "36px" }}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={12} sm={12} xs={12}>
                    <Form.Group className="" controlId="formAccessToken">
                      <Form.Label>Facebook Page Access Token</Form.Label>
                      <Form.Control
                        as="textarea"
                        required
                        name="access_token"
                        value={settingData?.access_token}
                        onChange={handleChange}
                        placeholder="Enter Page Access Token (Long-lived or permanent)"
                        rows={3}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>

            <Modal.Footer>
              <button className="btn btn-light" onClick={props.onHide}>
                Close
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={handleSubmit}
              >
                Save
              </button>
            </Modal.Footer>
          </>
        ) : (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Saving setting...</p>
          </div>
        )}
      </Modal>

      <ToastContainer autoClose={3000} />
    </>
  );
};

export default InstagramSettingEdit;
